import { QA_LOGIN_AUTOFILL } from 'flagSwitch/SwitchConfig';

/**
 * QA sign-in autofill for the two portal login screens.
 *
 * The seeded QA accounts (`app-backend/src/database/seedQaLogins.ts`) each have a
 * fixed OTP on a non-production backend (`QA_DEV_OTP_BY_EMAIL`). This fills the
 * rest of the form the moment the identifying value is in the field:
 *
 *   partner  the partner code   → email + password, then the OTP on step two
 *   crm      the email address  → password, then the OTP on step two
 *
 * It only FILLS — Sign In and Verify & Continue are still clicked by hand, so a
 * wrong-password or wrong-code case can still be tested on the same account.
 *
 * Both screens read it from here rather than each keeping its own copy, for the
 * same reason as `constants/rbacConfig.js`: two copies is how one screen ends up
 * filling while the other does not.
 *
 * ⚠️ Switched by `QA_LOGIN_AUTOFILL` in `flagSwitch/SwitchConfig.js` (strict
 * `=== true`). That switch is the same in every build, so while it is on these
 * credentials ship to every environment — turn it off before a production merge.
 */
export const QA_AUTOFILL_ENABLED = QA_LOGIN_AUTOFILL === true;

/*
  The literals live INSIDE the ternary on purpose: with the switch off the build
  folds `false ? {...} : null` to `null` and the credentials never reach the
  bundle. Hoisting any of them into a const outside it would ship them whether
  the switch is on or not.
*/
const QA_ACCOUNTS = QA_AUTOFILL_ENABLED
  ? {
      partner: {
        partnerCode: '4732178',
        email: 'qa-partner@zenvoya.ai',
        password: 't735tqpJ6fejo3kv',
        otp: '615924',
      },
      crm: {
        email: 'qa-admin@zenvoya.ai',
        password: 'dkRww6yHXM4aTR7d',
        otp: '342376',
      },
    }
  : null;

const sameEmail = (a, b) => String(a || '').trim().toLowerCase() === b;

/** Partner screen: `{ email, password }` for the QA partner code, else null. */
export const qaPartnerCredentialsFor = (partnerCode) => {
  const qa = QA_ACCOUNTS?.partner;
  if (!qa || partnerCode !== qa.partnerCode) return null;
  return { email: qa.email, password: qa.password };
};

/** CRM screen: the QA account's password for its email, else null. */
export const qaCrmPasswordFor = (email) => {
  const qa = QA_ACCOUNTS?.crm;
  if (!qa || !sameEmail(email, qa.email)) return null;
  return qa.password;
};

/**
 * Either screen's code step: the QA account's fixed OTP as the six box values,
 * else null. Keyed by portal so one door's code is never offered at the other —
 * the backend gives each door its own account and code for the same reason.
 */
export const qaOtpFor = (portal, email) => {
  const qa = QA_ACCOUNTS?.[portal];
  if (!qa || !sameEmail(email, qa.email)) return null;
  return qa.otp.split('');
};
